import React from "react";
import styled from "@emotion/styled";
import whatsappBg from "../assets/wa_bg.png";
import noImage from "../assets/no-image.jpg";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Typography } from "@mui/material";
import VideoPreview from "./VideoPreview";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
/* ---------- generic container ---------- */
const ChatContainer = styled.div`
  max-width: 420px;
  height: 640px;
  background: #e5ddd5 url(${whatsappBg});
  background-size: contain;
  font-family: "Segoe UI", Helvetica, Arial, sans-serif;
  border: 1px solid #e9e9e9;
  border-radius: 8px;
  overflow: hidden;
  margin: 24px auto;
  display: flex;
  flex-direction: column;
`;

/* ---------- header bar ---------- */
const ChatHeader = styled.div`
  height: 52px;
  background: #075e54;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  font-weight: 500;
  font-size: 15px;
`;

/* ---------- scrollable chat area ---------- */
const ChatContent = styled.div`
  flex: 1;
  padding: 20px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

/* ---------- bubble + message layout ---------- */
const Message = styled.div`
  align-self: flex-start;
  max-width: 82%;
  margin-bottom: 10px;
`;

const Bubble = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    border-right: 16px solid #fff;
    border-bottom: 10px solid transparent;
    left: -8px;
    top: 0;
  }
`;

/* ---------- component-specific bits ---------- */
const HeaderImage = styled.img`
  width: 100%;
  border-radius: 4px;
  object-fit: cover;
`;

const MediaShell = styled.div`
  width: 100%;
  height: 180px;
  background: #f2f2f2;
  border-radius: 4px;
  color: #555;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
`;

export const BodyText = styled.div`
  white-space: pre-wrap;
  font-size: 14px;
  line-height: 1.4;
  color: #303030;
`;

const FooterText = styled.div`
  font-size: 11px;
  color: #6b6b6b;
  margin-top: 12px;
  text-align: right;
`;

const ButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
  gap: 6px;
`;

const ChatButton = styled.a`
  background: #f8f8f8;
  border-radius: 18px;
  padding: 8px 16px;
  font-weight: 500;
  font-size: 13px;
  color: #075e54;
  text-align: center;
  text-decoration: none;
  transition: background 0.2s;

  &:hover {
    background: #e2e2e2;
  }
`;

/* ---------- carousel-specific bits ---------- */
const CarouselContainer = styled.div`
  display: flex;
  overflow-x: auto;
  margin-top: 12px;
  gap: 8px;
  padding-bottom: 8px;
`;

const Card = styled.div`
  min-width: 200px;
  background: #f8f8f8;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 0.5px rgba(0, 0, 0, 0.13);
`;

/* ---------- template meta bar ---------- */
const TemplateMeta = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border-top: 1px solid #e9e9e9;
  font-size: 11px;
  padding: 8px 16px;
  color: #666;
  line-height: 1.3;
`;

/* ===================================================================== */
/* =======================  MAIN PREVIEW COMPONENT ===================== */
/* ===================================================================== */
const TemplatePreview = ({ template, previewUrl = "", fileName = "" }) => {
  /* 🔹 util — replace {{1}} etc. with bullets for preview */
  /* 🔹 render each component type */
  const renderComponent = (c, options = {}) => {
    const { overridePreviewUrl } = options;

    switch (c.type) {
      /* --------------- HEADER --------------- */
      case "HEADER": {
        const src = overridePreviewUrl || previewUrl || noImage;
        if (c.format === "IMAGE") {
          return (
            <HeaderImage
              src={src}
              alt="header media"
              onError={(e) => (e.target.src = noImage)}
            />
          );
        }
        if (c.format === "VIDEO") {
          return <VideoPreview viewUrl={src} />;
        }
        if (c.format === "DOCUMENT") {
          const src = overridePreviewUrl || previewUrl || "";
          // try to extract name from src if possible, else fallback
          const filename = c.filename || src.split("/").pop() || "document.pdf";

          if (src) {
            return (
              <div>
                <MediaShell style={{ padding: 0 }}>
                  <iframe
                    src={src}
                    title="Document Preview"
                    width="100%"
                    height="100%"
                    style={{ border: "none", borderRadius: "4px" }}
                  />
                </MediaShell>
                <Typography
                  variant="caption"
                  sx={{ display: "block", textAlign: "center", mt: 0.5 }}
                >
                  <InsertDriveFileIcon fontSize="inherit" /> {filename}
                </Typography>
              </div>
            );
          }

          return (
            <MediaShell>
              <InsertDriveFileIcon /> &nbsp; {filename}
            </MediaShell>
          );
        }

        if (c.format === "LOCATION") {
          return (
            <MediaShell>
              <LocationOnOutlinedIcon />
            </MediaShell>
          );
        }
        return (
          <Typography fontWeight={"bold"} fontSize={"16px"}>
            {c.text}
          </Typography>
        );
      }

      /* --------------- BODY --------------- */
      case "BODY":
        return <BodyText>{c.text}</BodyText>;

      /* --------------- FOOTER --------------- */
      case "FOOTER":
        return <FooterText>{c.text}</FooterText>;

      /* --------------- BUTTONS --------------- */
      case "BUTTONS":
        return (
          <ButtonWrap>
            {c.buttons?.map((btn, i) => {
              let href = "#";
              if (btn.type === "PHONE_NUMBER") href = `tel:${btn.phone_number}`;
              if (btn.type === "URL") href = btn.url;
              return (
                <ChatButton
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {btn.type === "COPY_CODE" ? "Copy Code" : btn.text}
                </ChatButton>
              );
            })}
          </ButtonWrap>
        );

      /* --------------- CAROUSEL --------------- */
      case "CAROUSEL":
        return (
          <CarouselContainer>
            {c.cards?.map((card, index) => (
              <Card key={index}>
                {card.components.map((comp, compIdx) => {
                  const overrideUrl = comp.example?.header_handle?.[0];
                  return (
                    <React.Fragment key={compIdx}>
                      {renderComponent(comp, {
                        overridePreviewUrl: overrideUrl,
                      })}
                    </React.Fragment>
                  );
                })}
              </Card>
            ))}
          </CarouselContainer>
        );

      default:
        return null;
    }
  };

  return (
    <ChatContainer>
      {/* top bar */}
      <ChatHeader>
        <span>{template.name}</span>
        <span style={{ fontSize: 12 }}>Preview</span>
      </ChatHeader>

      {/* chat bubble content */}
      <ChatContent>
        <Message>
          <Bubble>
            {template.components.map((c, idx) => (
              <React.Fragment key={idx}>{renderComponent(c)}</React.Fragment>
            ))}
          </Bubble>
        </Message>
      </ChatContent>

      {/* meta info */}
      <TemplateMeta>
        {template.name} &nbsp;|&nbsp; {template.id} &nbsp;|&nbsp;{" "}
        {template.language} &nbsp;|&nbsp; {template.category ?? "—"}{" "}
        &nbsp;|&nbsp; {template.status}
      </TemplateMeta>
    </ChatContainer>
  );
};

export default TemplatePreview;
